<script setup lang='ts'>
import type { FormInst, FormItemInst, FormItemRule, FormRules, SelectOption } from 'naive-ui'

import { isBoolean } from '@qctsw/utils'
import type { CarSelected } from '~/components/Basic/CarSelectFilter/hooks'
import { CAR_INSURANCE_CODE, ISSUE_TYPE_CODE_ENUM } from '~/shared/constants'
import { validateHandle } from '~/shared/function'
import type { CaseCollectiveResponse, CollectiveDetailResponse, DealerResponse, ScanDrivingResponse } from '@/server/types'
import { FILE_UPLOAD_TYPE, LOAD_STATE } from '@/shared/types'
import { collectiveHotApi } from '@/server'

const emits = defineEmits({
  changeCurrentStep: (_step: number) => true,
})

const { formData } = storeToRefs(usePublishStore())
const formRef = ref<FormInst | null>(null)
const rules: FormRules = {
  carRelationIsTrue: [
    {
      validator: (_rule: FormItemRule, _value) => {
        const { carBrandId, carSeriesId } = formData.value
        if (!carBrandId)
          return new Error('请选择车辆品牌')
        else if (!carSeriesId)
          return new Error('请选择车辆车系')
        // else if (!carModelId)
        //   return new Error('请选择车辆车型')

        return true
      },
      trigger: 'change',
    },
  ],
  firstFaultTime: [
    {
      required: true,
      message: '请选择首次出现问题日期',
      trigger: 'blur',
    },
  ],

  carDealerPhone: [
    {
      required: true,
      message: '请填写经销商电话',
      trigger: 'blur',
    },
  ],
  isWillingToLive: [
    {
      validator: (_rule: FormItemRule, _value) => {
        const { isWillingToLive } = formData.value

        if (!isBoolean(isWillingToLive))
          return new Error('请选择是否同意直播连线')

        return true
      },
      message: '请选择是否同意直播连线',
      trigger: 'blur',
    },
  ],

  complainMoney: [

    {
      validator: (_rule: FormItemRule, _value) => {
        const { complainMoney } = formData.value
        console.log('🚀 ~ complainMoney:', complainMoney)

        if (!complainMoney)
          return new Error('涉诉金额需大于0')

        return true
      },
      message: '涉诉金额需大于0',
      trigger: 'blur',
    },
  ],
  carInsuranceCode: [
    {
      validator: (_rule: FormItemRule, _value) => {
        const { carInsuranceCode } = formData.value
        if (!carInsuranceCode || !CAR_INSURANCE_CODE.map(v => v.value).includes(carInsuranceCode))
          return new Error('请选择车保类型')

        return true
      },
      trigger: 'blur',
    },
  ],
  carDealerAreaIds: [
    {
      validator: (_rule: FormItemRule, value: [string | undefined, string | undefined]) => {
        const [provinceId, cityId] = value
        if (!provinceId || !cityId)
          return new Error('请选择经销商所在地区')
        return true
      },
    },
  ],
  carDealerName: [
    {
      required: true,
      message: '请选择经销商',
      trigger: 'change',
    },
  ],
  carRegionPlate: [
    {
      validator: (_rule: FormItemRule, _value) => {
        const { carRegionPlate, carNumberPlate } = formData.value
        if (!carRegionPlate || (carRegionPlate !== '未上牌' && !carNumberPlate))
          return new Error('请输入车牌号')

        return true
      },
    },
  ],
  carShoppingTime: [
    {
      required: true,
      message: '选择购车/上牌日期',
      trigger: 'blur',
    },
  ],
}

const carSelectedFormItemRef = ref<FormItemInst | null>(null)
function carSelected(_data: CarSelected) {
  // console.log('🚀 ~ carSelected ~ _data:', _data)

  formData.value.carSeriesEnergyCode = _data.series?.carSeriesEnergyCode
  formData.value.carBrandName = _data.brand?.brandName
  formData.value.carBrandId = _data.brand?.id ? Number(_data.brand?.id) : undefined

  formData.value.carBrandVendorId = _data.series?.brandVendorId ? Number(_data.series?.brandVendorId) : undefined
  formData.value.carVendorName = _data.series?.carVendorName
  formData.value.carVendorId = _data.series?.carVendorId ? Number(_data.series?.carVendorId) : undefined
  formData.value.carSeriesId = _data.series?.id ? Number(_data.series?.id) : undefined
  formData.value.carSeriesName = _data.series?.carSeriesName

  formData.value.carModelId = _data.model?.id ? Number(_data.model?.id) : undefined
  formData.value.carModelName = _data.model?.carModelName

  if (!_data.isInit)
    carSelectedFormItemRef.value?.validate({ trigger: 'change' })
}
function changeDealerHandler(d: DealerResponse) {
  formData.value.carDealerId = d.id === -1 ? undefined : d.id
  formData.value.carDealerName = d.dealerNameSimple
  formData.value.carDealerLinkman = d.dealerLinkman
  formData.value.carDealerPhone = d.dealerPhone
}

const drivingLicenseFrontImg = ref<string | undefined>(undefined)
// 行驶证识别
function scanHandle(res: ScanDrivingResponse) {
  // console.log('scanHandle', res)
  formData.value.carBrandId = res.carBrandId ? Number(res.carBrandId) : formData.value.carBrandId
  formData.value.carSeriesId = res.carSeriesId ? Number(res.carSeriesId) : formData.value.carSeriesId
  formData.value.carModelId = res.carModelId ? Number(res.carModelId) : formData.value.carModelId

  formData.value.carRegionPlate = res.carRegionPlate ?? undefined
  formData.value.carNumberPlate = res.carNumberPlate ?? undefined
  formData.value.carVin = res.carVin ?? undefined
  formData.value.carEngineNumber = res.carEngineNumber ?? undefined

  useMessage.success('图片识别成功，行驶证信息已自动填写/替换', { closable: true })
}

async function validator() {
  const { isOk } = await validateHandle(formRef)
  if (!isOk)
    return false

  emits('changeCurrentStep', 2)
}

const isCollectTitle = ref(false)

const collectiveHotList = ref<CollectiveDetailResponse[]>([])
const state = ref<LOAD_STATE>(LOAD_STATE.INIT)

async function getCollectiveHotList(params: { collectiveId?: number, title?: string } = {}) {
  isCollectTitle.value = !!params?.collectiveId || !!params?.title
  const res = await useLoadState({

    state,
    fn: () => collectiveHotApi({ ...params, size: 10 }),
  })

  collectiveHotList.value = res?.data || []
}

function handleSearch(e: string) {
  // isCollectTitle.value = !!e
  getCollectiveHotList({ title: e })
  // console.log('🚀 ~ handleSearch ~ e:', e)
}
defineExpose({
  formRef,
  getCollectiveHotList,
})
await getCollectiveHotList({ collectiveId: formData.value.collectiveId || undefined })
function renderLabel(option: SelectOption) {
  if (isCollectTitle.value)
    return option.title as string

  return [
    h(

      'span',
      {
        style: {
          verticalAlign: '-0.15em',
          marginRight: '4px',
        },
        class: 'i-mdi:hot text-red-5',
      },
      {
        default: () => h('i'),
      },

    ),
    option.title as string,
  ]
}

function updateCollective(value: string, o: CollectiveDetailResponse) {
  if (!value)
    return
  const { issueLs, seriesLs } = o

  const brand = seriesLs?.[0]
  formData.value.carBrandName = brand?.brandName
  formData.value.carBrandId = brand?.brandId

  formData.value.carSeriesName = brand?.seriesName
  formData.value.carSeriesId = brand?.seriesId
  formData.value.appeal = issueLs.filter(i => i.issueTypeCode === ISSUE_TYPE_CODE_ENUM.appeal).map(i => i.issueId)
  formData.value.carDealerAreaIds = [null, undefined]
  formData.value.carDealerName = undefined
}
</script>

<template>
  <div>
    <div class="bg-white rounded-md p-4xl mt-4xl relative">
      <UtilScanDriving v-model="drivingLicenseFrontImg" class="flex justify-end" @scan-handle="scanHandle" />

      <NForm
        ref="formRef"
        :model="formData"
        :rules="rules"
        :label-width="150"
        label-placement="left"
        class="px-20"
        size="medium"
      >
        <NGrid :cols="24" :x-gap="24">
          <NFormItemGi first :span="15" path="carTravelKm" label="集体投诉">
            <NSelect
              v-model:value="formData.collectiveId"
              clearable
              remote
              filterable
              label-field="title"
              placeholder="请搜索并选择集体投诉"
              value-field="collectiveId"
              :render-label="renderLabel"
              :options="collectiveHotList"
              @update:value="updateCollective"
              @search="handleSearch"
            />
          </NFormItemGi>

          <NFormItemGi required first :show-feedback="true" :span="24" path="isWillingToLive" label="直播连线">
            <n-radio-group v-model:value="formData.isWillingToLive" name="isWillingToLive">
              <n-space>
                <n-radio :value="true">
                  同意
                </n-radio>

                <n-radio :value="false">
                  不同意
                </n-radio>
              </n-space>
            </n-radio-group>
          </NFormItemGi>
          <NFormItemGi first :span="24" :label-width="150" required label="">
            <dv class="text-slate-4 text-3 ml-37.5 ">
              工作人员为了更好帮您解决问题，可能需要与你连线沟通，这个连线有可能是投诉网工作人员的连线，也有可能是其它媒体的连线，或者是多方连线。连线的目的是为了更好、更快的解决问题。
            </dv>
          </NFormItemGi>

          <NFormItemGi ref="carSelectedFormItemRef" first :span="24" path="carRelationIsTrue" required label="车型信息">
            <BasicCarSelectFilter
              v-model:brandId="formData.carBrandId"
              v-model:seriesId="formData.carSeriesId"
              v-model:modelId="formData.carModelId"
              :show-footer="true"
              @change="carSelected"
            />
          </NFormItemGi>

          <NFormItemGi first :span="24" label="首次出现问题日期" required :show-feedback="false">
            <NFormItemGi first :span="5" path="firstFaultTime" :show-label="false">
              <NDatePicker v-model:formatted-value="formData.firstFaultTime" value-format="yyyy-MM-dd" type="date" placeholder="请选择购车/上牌日期" />
            </NFormItemGi>
            <NFormItemGi class="ml-sm" first :span="10" path="carInsuranceCode" :show-label="false">
              <NRadioGroup v-model:value="formData.carInsuranceCode">
                <NRadioButton v-for="v in CAR_INSURANCE_CODE" :key="v.value" :value="v.value" :label="v.label" />
              </NRadioGroup>
            </NFormItemGi>
          </NFormItemGi>

          <NFormItemGi first :span="24" path="carTravelKm" label="已行驶里程(km)">
            <NInput v-model:value="formData.carTravelKm" class="max-w-45" placeholder="请输入已行驶里程(km)" />
          </NFormItemGi>

          <NFormItemGi required first :span="24" path="complainMoney" label="涉诉金额(万元)">
            <n-input-number v-model:value="formData.complainMoney" :show-button="false" :precision="2" :min="0" class="max-w-45" placeholder="请输入涉诉金额(万元)">
              <template #suffix>
                <span class="font-bold color-primary">万元</span>
              </template>
            </n-input-number>
          </NFormItemGi>
          <NFormItemGi first :span="24" path="carDealerAreaIds" required label="经销商所在地">
            <div class="flex items-center w-full">
              <BasicAreaSelect
                v-model:province-id="formData.carDealerAreaIds[0]"
                v-model:city-id="formData.carDealerAreaIds[1]"
                class="max-w-100"
                :is-area="false"
              />
              <div class="text-slate-4 text-3 ml-3">
                准确选择有利于厂家准确判断，提供更快捷的处理意见!
              </div>
            </div>
          </NFormItemGi>
          <NFormItemGi first :span="24" path="carDealerName" required label="经销商名称">
            <div class="flex items-center ">
              <BasicDealerSelect
                :province-id="formData.carDealerAreaIds[0]"
                :city-id="formData.carDealerAreaIds[1]"
                :series-id="formData.carSeriesId"
                :custom-dealer="!formData.carDealerId ? formData.carDealerName : ''"
                @dealer-select="changeDealerHandler"
              >
                <button
                  class="border-1 border-slate-3 rounded-md px-sm py-1  text-3.5"
                  :class="[formData.carDealerName ? 'bg-blue-5 text-white' : 'text-slate-4']"
                >
                  {{ formData.carDealerName || '选择经销商' }}
                </button>
              </BasicDealerSelect>
              <div class="text-slate-4 text-3 ml-3">
                如选项中无相关经销商，可直接在输入框中输入经销商名字，进行创建并选择
              </div>
            </div>
          </NFormItemGi>
          <NFormItemGi first required :span="24" path="carDealerPhone" label="经销商电话">
            <div class="flex items-center w-full">
              <NInput v-model:value="formData.carDealerPhone" class="max-w-45" placeholder="请输入经销商电话" />
              <div class="text-slate-4 text-3 ml-3">
                请详细填写4S店联系方式！
              </div>
            </div>
          </NFormItemGi>
          <NFormItemGi first :span="24" path="carDealerLinkman" label="经销商联系人">
            <NInput v-model:value="formData.carDealerLinkman" class="max-w-45" placeholder="请输入经销商联系人" />
          </NFormItemGi>
          <NFormItemGi first :span="24" path="carVin" label="车架号">
            <div class="flex items-center w-full">
              <NInput v-model:value="formData.carVin" class="max-w-45" placeholder="请输入车架号" />
              <div class="text-slate-4 text-3 ml-3">
                (行驶证上可查找)如实填写对厂家了解情况更有帮助！部分厂家需要车架号才能受理投诉！
              </div>
            </div>
          </NFormItemGi>
          <NFormItemGi first :span="24" path="carEngineNumber" label="发动机号">
            <div class="flex items-center w-full">
              <NInput v-model:value="formData.carEngineNumber" class="max-w-45" placeholder="请输入发动机号" />
              <div class="text-slate-4 text-3 ml-3">
                (行驶证上可查找)如实填写对厂家了解情况更有帮助！部分厂家需要发动机号才能受理投诉！
              </div>
            </div>
          </NFormItemGi>
          <NFormItemGi first :span="24" path="carRegionPlate" required label="车牌号">
            <div class="flex">
              <BasicCarPlateModal v-model="formData.carRegionPlate" class="mr-sm px-lg py-1 rounded border-1 border-slate-2" :class="[formData.carRegionPlate ? 'bg-blue-5 text-white' : 'text-slate-4']" />
              <NInput v-show="formData.carRegionPlate && formData.carRegionPlate !== '未上牌'" v-model:value="formData.carNumberPlate" class="w-40" placeholder="请输入车牌号" />
            </div>
          </NFormItemGi>
          <NFormItemGi :span="24" path="carShoppingTime" label="购车/上牌日期">
            <div class="flex items-center">
              <NDatePicker v-model:formatted-value="formData.carShoppingTime" value-format="yyyy-MM-dd" type="date" placeholder="请选择购车/上牌日期" />
              <div class="text-slate-4 text-3 ml-3">
                准确选择有利于厂家准确判断，提供更快捷的处理意见!
              </div>
            </div>
          </NFormItemGi>
        </NGrid>
      </NForm>
    </div>
    <div class="flex items-center justify-center mt-xl">
      <button class="btn-default px-4xl mr-xl" @click="emits('changeCurrentStep', 0)">
        上一步
      </button>
      <button class="btn-primary px-4xl" @click="validator">
        下一步
      </button>
    </div>
  </div>
</template>

<style lang='scss' scoped>
:deep(.n-radio-button.n-radio-button--checked){
  --uno: bg-blue-5 text-white;
}
</style>
